import { Component, Input, OnInit } from '@angular/core';
import { InfoItem } from '@frk/eds-components';
import { NAV } from '@models';
import { EMDASH } from '@products/utils/constants/product.constants';
import { TranslateService } from '@shared/translate/translate.service';
import { FundId, InfoItemWithKey, PricingConfig } from '@types';
import { fromLocaleNumber } from '@utils/il8n/number-il8n';

@Component({
  selector: 'ft-share-prices',
  templateUrl: './share-prices.component.html',
  styleUrls: ['./share-prices.component.scss'],
})
export class SharePricesComponent implements OnInit {
  @Input() navData: NAV;
  @Input() config: PricingConfig;
  @Input() fundId: FundId;
  infoItems: InfoItem[][] = [[], []];
  private infoItemsWithKey: InfoItemWithKey[][] = [[], []];
  valueDirection: string;

  constructor(private translateService: TranslateService) {}

  ngOnInit(): void {
    if (this.config.showApplicationPrice) {
      this.infoItemsWithKey[0].push({
        key: 'APPLICATIONPRICE',
        label: this.translateService.instant(
          'pds.APPLICATION_PRICE',
          this.fundId
        ),
        val: this.navData.popValue,
        caveatPlacement: 'Price:ApplicationPricePriceDistribution',
      });
    }

    if (this.config.showRedemptionPrice) {
      this.infoItemsWithKey[0].push({
        key: 'REDEMPTIONPRICE',
        label: this.translateService.instant(
          'pds.REDEMPTION_PRICE',
          this.fundId
        ),
        val: this.navData.redemptionPrice,
        caveatPlacement: 'Price:RedemptionPricePriceDistribution',
      });
    }

    this.infoItemsWithKey[0].push(
      {
        key: 'NAV',
        label: this.translateService.instant('pds.NAV_VALUE', this.fundId),
        val: this.navData.navValue,
        labelSmallText: this.translateService.instant(
          'products.nav-desc',
          this.fundId
        ),
        caveatPlacement: 'Price:NavPriceDistribution',
      },
      {
        key: 'NAV_CHANGE',
        label: this.translateService.instant(
          'pds.DAILY_NAV_CHANGE',
          this.fundId
        ),
        val: this.navData.navChangeValue,
        caveatPlacement: 'Price:NavChangePriceDistribution',
      },
      {
        key: 'NAV_CHANGE_PERCENT',
        label: this.translateService.instant(
          'pds.DAILY_NAV_CHANGE_PERCENTAGE',
          this.fundId
        ),
        val: this.navData.navChangePercentage,
        caveatPlacement: 'Price:NavChangePercentPriceDistribution',
      }
    );

    if (this.config.showPop) {
      this.infoItemsWithKey[1].push(this.getPopData());
    }

    if (this.config.showMarketPriceData) {
      this.infoItemsWithKey[1] = [
        ...this.infoItemsWithKey[1],
        ...this.getMarketPriceData(),
      ];
    }

    if (this.config.showMoneyMarketPriceData) {
      this.infoItemsWithKey[1] = [
        ...this.infoItemsWithKey[1],
        ...this.getMoneyMarketPriceData(),
      ];
    }

    if (this.config.productType === 'Money Market Funds') {
      this.infoItemsWithKey[1] = [
        ...this.infoItemsWithKey[1],
        ...this.getIntlMoneyMarketNavData(),
      ];
    }

    this.infoItems = this.infoItemsWithKey.map((items) =>
      items.map((itemWithKey) => this.getInfoItem(itemWithKey))
    );

    if (this.infoItems[1].length === 0) {
      const firstColumnLength = Math.ceil(this.infoItems[0].length / 2);
      this.infoItems[1] = this.infoItems[0].slice(firstColumnLength);
      this.infoItems[0] = this.infoItems[0].slice(0, firstColumnLength);
    }
  }

  private getPopData(): InfoItemWithKey {
    return {
      key: 'POP',
      label: this.translateService.instant('products.POP_VALUE', this.fundId),
      val: this.navData.popValue,
      labelSmallText: this.translateService.instant(
        'products.pop-desc',
        this.fundId
      ),
      caveatPlacement: 'Price:PopPriceDistribution',
    };
  }

  private getMarketPriceData(): InfoItemWithKey[] {
    return [
      {
        key: 'MARKET_PRICE',
        label: this.translateService.instant(
          'products.market-price-value',
          this.fundId
        ),
        val: this.navData.marketPriceValue,
        caveatPlacement: 'Price:MarketPriceDistribution',
      },
      {
        key: 'MARKET_PRICE_CHANGE',
        label: this.translateService.instant(
          'products.market-price-change',
          this.fundId
        ),
        val: this.navData.marketPriceChangeValue,
        caveatPlacement: 'Price:MarketPriceChangeDistribution',
      },
      {
        key: 'MARKET_PRICE_CHANGE_PERCENT',
        label: this.translateService.instant(
          'products.market-price-change-percent',
          this.fundId
        ),
        val: this.navData.marketPriceChangePercentage,
        caveatPlacement: 'Price:MarketPriceChangePercentDistribution',
      },
    ];
  }

  private getMoneyMarketPriceData(): InfoItemWithKey[] {
    const moneyMarketInfoItem = [];
    if (this.navData?.moneyFundNav) {
      moneyMarketInfoItem.push({
        key: 'MONEY_FUND_NAV_VALUE',
        label: this.translateService.instant(
          'products.Fund-Level-Market-Nav',
          this.fundId
        ),
        val: this.navData.moneyFundNav,
        caveatPlacement: 'Price:MoneyFundNavDistribution',
      });
    }
    if (this.navData?.moneyFundMarketNav) {
      moneyMarketInfoItem.push({
        key: 'MONEY_FUND_MARKET_NAV_VALUE',
        label: this.translateService.instant(
          'products.Master-Fund-Level-Market-Nav',
          this.fundId
        ),
        val: this.navData.moneyFundMarketNav,
        caveatPlacement: 'Price:MonetFundMarketNavDistribution',
      });
    }
    if (this.navData?.intradayNav && !this.config.hideIntraDayNav) {
      moneyMarketInfoItem.push({
        key: 'INTRADAY_NAV_VALUE',
        label: this.translateService.instant(
          'products.IntradayNav',
          this.fundId
        ),
        val: this.navData.intradayNav,
        caveatPlacement: 'Price:IntraDayNavDistribution',
      });
    }
    if (this.navData?.dailyLiquidAssetRatio) {
      moneyMarketInfoItem.push({
        key: 'DAILY_LIQUID_ASSET_RATIO',
        label: this.translateService.instant(
          'products.Liquid-Asset-Daily',
          this.fundId
        ),
        val: this.navData.dailyLiquidAssetRatio,
        caveatPlacement: 'Price:DailyLiquidAssetRatioDistribution',
      });
    }
    if (this.navData?.weeklyLiquidAssetRatio) {
      moneyMarketInfoItem.push({
        key: 'WEEKLY_LIQUID_ASSET_RATIO',
        label: this.translateService.instant(
          'products.Liquid-Asset-Weekly',
          this.fundId
        ),
        val: this.navData.weeklyLiquidAssetRatio,
        caveatPlacement: 'Price:DailyLiquidAssetRatioDistribution',
      });
    }
    return moneyMarketInfoItem;
  }

  private getIntlMoneyMarketNavData(): InfoItemWithKey[] {
    const moneyMarketInfoItem = [];
    if (this.navData?.marketToMarketNav) {
      moneyMarketInfoItem.push({
        key: 'MARKET_TO_MARKET_NAV',
        label: this.translateService.instant('pds.MTM_NAV', this.fundId),
        val: this.navData.marketToMarketNav,
        caveatPlacement: 'Price:MarketToMarketNav',
      });
    }
    if (this.navData?.marketToMarketNavChg) {
      moneyMarketInfoItem.push({
        key: 'MARKET_TO_MARKET_NAV_CHG',
        label: this.translateService.instant('pds.MTM_NAV_CHG', this.fundId),
        val: this.navData.marketToMarketNavChg,
        caveatPlacement: 'Price:MarketToMarketNavChg',
      });
    }
    return moneyMarketInfoItem;
  }

  private getInfoItem(infoItem: InfoItemWithKey): InfoItem {
    let valueDirection: 'gain' | 'static' | 'loss';
    if (
      infoItem.key === 'NAV_CHANGE' ||
      infoItem.key === 'NAV_CHANGE_PERCENT' ||
      infoItem.key === 'MARKET_PRICE_CHANGE' ||
      infoItem.key === 'MARKET_PRICE_CHANGE_PERCENT'
    ) {
      const floatRegex = /[+-]?[0-9]*[.]?[0-9]+/g;
      const navchangePercentage = this.infoItemsWithKey.length
        ? Math.sign(parseFloat(infoItem.val.match(floatRegex)?.join('.')))
        : 0;

      if (navchangePercentage === 1) {
        valueDirection = 'gain';
      } else if (navchangePercentage === -1) {
        valueDirection = 'loss';
      } else {
        // *** REQUIREMENT FIX: NGC-4420 ***
        // If nav change is negative and by round off, % is coming static as 0.00; then, manually make it to red.
        if (infoItem.key === 'NAV_CHANGE_PERCENT') {
          const navChangeValueSign = Math.sign(
            fromLocaleNumber(this.navData.navChangeValue)
          );

          if (navChangeValueSign === 1) {
            valueDirection = 'gain';
          } else if (navChangeValueSign === -1) {
            valueDirection = 'loss';
          } else {
            valueDirection = 'static';
          }
        } else {
          valueDirection = 'static';
        }
      }
    }

    return {
      label: infoItem.label,
      val: infoItem.val ? infoItem.val : EMDASH,
      valueDirection,
      labelSmallText: infoItem.labelSmallText,
      caveatPlacement: infoItem.caveatPlacement,
    };
  }
}
